import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useWebsiteStoreOrders,
  useWebsiteStoreProducts,
  useWebsiteStoreSettings,
  type WebsiteStoreOrder,
  type WebsiteStoreProduct,
} from '../hooks/useWebsiteStore';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background:
    radial-gradient(circle at 8% 2%, rgba(59, 130, 246, 0.14), transparent 42%),
    radial-gradient(circle at 92% 0%, rgba(14, 165, 233, 0.16), transparent 36%),
    var(--background);
  padding: clamp(1rem, 2.4vw, 1.85rem);
  min-height: 100%;
  display: grid;
  gap: 1.1rem;
`;

const Header = styled.div`
  position: relative;
  overflow: hidden;
  padding: clamp(1rem, 2.1vw, 1.45rem);
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: linear-gradient(140deg, color-mix(in srgb, var(--surface) 88%, transparent), color-mix(in srgb, var(--surfaceVariant) 92%, transparent));
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;

  &::after {
    content: '';
    position: absolute;
    right: -70px;
    top: -70px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(14, 165, 233, 0.28), transparent 68%);
    pointer-events: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
`;

const HeaderMain = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 0.65rem;
`;

const HeaderKicker = styled.div`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  border-radius: 9999px;
  border: 1px solid color-mix(in srgb, var(--primary) 30%, var(--outline));
  background: color-mix(in srgb, var(--primary) 16%, var(--surface));
  color: var(--onSurface);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.28rem 0.62rem;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.45rem, 2.6vw, 2rem);
  line-height: 1.08;
  letter-spacing: -0.01em;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0;
  font-size: 0.95rem;
  max-width: 760px;
  line-height: 1.45;
`;

const HeaderStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const HeaderStat = styled.div`
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: color-mix(in srgb, var(--surface) 94%, transparent);
  padding: 0.5rem 0.66rem;
  min-width: 110px;

  strong {
    display: block;
    color: var(--onSurface);
    font-size: 1rem;
    line-height: 1.15;
  }

  span {
    display: block;
    font-size: 0.72rem;
    color: var(--onSurfaceVariant);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 0.15rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SingleColumn = styled.div`
  display: grid;
  gap: 1rem;
`;

const TabStrip = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
`;

const TabRow = styled.div`
  display: inline-flex;
  gap: 0.42rem;
  padding: 0.34rem;
  border-radius: 9999px;
  border: 1px solid color-mix(in srgb, var(--outline) 84%, transparent);
  background: linear-gradient(180deg, color-mix(in srgb, var(--surfaceVariant) 90%, var(--surface)), var(--surface));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 9999px;
  padding: 0.56rem 1.02rem;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease, transform 150ms ease, box-shadow 150ms ease;
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 90%, #ffffff), var(--primary))'
      : 'transparent'};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  box-shadow: ${({ $active }) => ($active ? '0 6px 16px rgba(15, 23, 42, 0.2)' : 'none')};

  &:hover {
    transform: translateY(-1px);
    color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurface)')};
  }
`;

const Card = styled.section`
  background: linear-gradient(170deg, color-mix(in srgb, var(--surface) 92%, #ffffff 8%), var(--surface));
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  border-radius: 18px;
  box-shadow: 0 16px 32px rgba(17, 24, 39, 0.08);
  padding: clamp(1rem, 2vw, 1.2rem);
`;

const CardTitle = styled.h2`
  margin: 0 0 0.4rem;
  color: var(--onSurface);
  font-size: 1.06rem;
  letter-spacing: -0.01em;
`;

const CardHint = styled.p`
  margin: 0 0 1.2rem;
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
  line-height: 1.45;
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const InlineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 0.45rem;
  min-width: 0;
  color: var(--onSurface);
  font-size: 0.84rem;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

const FormSection = styled.div`
  display: grid;
  gap: 0.95rem;
  padding-top: 0.2rem;

  & + & {
    margin-top: 0.2rem;
    padding-top: 1rem;
    border-top: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  }
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  font: inherit;
  line-height: 1.35;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--surfaceVariant) 70%, var(--surface));
  transition: border-color 140ms ease, box-shadow 140ms ease;

  &::placeholder {
    color: color-mix(in srgb, var(--onSurfaceVariant) 84%, transparent);
  }

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 50%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const TextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  border-radius: 12px;
  padding: 0.82rem 0.9rem;
  font: inherit;
  line-height: 1.45;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--surfaceVariant) 70%, var(--surface));
  min-height: 126px;
  resize: vertical;
  transition: border-color 140ms ease, box-shadow 140ms ease;

  &::placeholder {
    color: color-mix(in srgb, var(--onSurfaceVariant) 84%, transparent);
  }

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 50%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const Select = styled.select`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  font: inherit;
  line-height: 1.35;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--surfaceVariant) 70%, var(--surface));

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 50%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--onSurface);
  font-size: 0.88rem;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const FormActions = styled(Actions)`
  margin-top: 0.2rem;
`;

const Button = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  border: 1px solid ${({ $variant }) => ($variant === 'secondary' ? 'color-mix(in srgb, var(--outline) 82%, transparent)' : 'transparent')};
  background: ${({ $variant }) =>
    $variant === 'danger'
      ? 'linear-gradient(135deg, color-mix(in srgb, var(--error) 90%, #ffffff), var(--error))'
      : $variant === 'secondary'
        ? 'linear-gradient(180deg, color-mix(in srgb, var(--surfaceVariant) 70%, var(--surface)), var(--surface))'
        : 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 90%, #ffffff), var(--primary))'};
  color: ${({ $variant }) => ($variant === 'secondary' ? 'var(--onSurface)' : 'var(--onPrimary)')};
  border-radius: 9999px;
  padding: 0.65rem 1rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 140ms ease, box-shadow 140ms ease, opacity 140ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(15, 23, 42, 0.18);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background: linear-gradient(135deg, color-mix(in srgb, var(--primary) 90%, #ffffff), var(--primary));
  color: var(--onPrimary);
  border-radius: 9999px;
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.18);
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 140ms ease, opacity 140ms ease;

  &:hover {
    transform: translateY(-1px);
  }

  input {
    display: none;
  }

  &.disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const Notice = styled.p<{ $error?: boolean }>`
  margin: 0;
  border-radius: 12px;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${({ $error }) => ($error ? 'color-mix(in srgb, var(--error) 40%, transparent)' : 'color-mix(in srgb, var(--outline) 82%, transparent)')};
  background: ${({ $error }) => ($error ? 'color-mix(in srgb, var(--error) 12%, var(--surface))' : 'color-mix(in srgb, var(--surfaceVariant) 66%, var(--surface))')};
  color: ${({ $error }) => ($error ? 'var(--error)' : 'var(--onSurfaceVariant)')};
  font-size: 0.85rem;
`;

const TableWrap = styled.div`
  margin-top: 0.4rem;
  overflow-x: auto;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  border-radius: 14px;
  background: color-mix(in srgb, var(--surface) 92%, transparent);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.95rem 0.9rem;
    border-bottom: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
    text-align: left;
    font-size: 0.88rem;
    color: var(--onSurfaceVariant);
    vertical-align: top;
  }

  th {
    background: color-mix(in srgb, var(--surfaceVariant) 80%, var(--surface));
    font-size: 0.74rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  tbody tr {
    transition: background 120ms ease;
  }

  tbody tr:hover {
    background: color-mix(in srgb, var(--surfaceVariant) 50%, var(--surface));
  }
`;

const ProductCell = styled.div`
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
`;

const ProductThumb = styled.div<{ $src?: string | null }>`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: ${({ $src }) => ($src ? `center / cover no-repeat url(${$src})` : 'var(--surfaceVariant)')};
  border: 1px solid color-mix(in srgb, var(--outline) 80%, transparent);
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
`;

const ProductTitle = styled.div`
  color: var(--onSurface);
  font-weight: 700;
  line-height: 1.35;
  margin-bottom: 0.14rem;
`;

const Muted = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
  line-height: 1.35;
`;

const StatusPill = styled.span<{ $tone?: 'success' | 'warning' | 'neutral' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.22rem 0.58rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${({ $tone }) =>
    $tone === 'success'
      ? 'rgba(16, 185, 129, 0.12)'
      : $tone === 'warning'
        ? 'rgba(245, 158, 11, 0.16)'
        : $tone === 'danger'
          ? 'rgba(239, 68, 68, 0.14)'
          : 'var(--surfaceVariant)'};
  color: ${({ $tone }) =>
    $tone === 'success'
      ? 'var(--success)'
      : $tone === 'warning'
        ? '#b45309'
        : $tone === 'danger'
          ? 'var(--error)'
          : 'var(--onSurfaceVariant)'};
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const OrderCardGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const DetailLabel = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.div`
  color: var(--onSurface);
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

type ProductFormState = {
  id?: string;
  slug: string;
  sku: string;
  title: string;
  short_description: string;
  description: string;
  product_type: 'physical' | 'digital';
  price_cents: string;
  image_url: string;
  external_url: string;
  external_label: string;
  status: 'draft' | 'published' | 'archived';
  sort_order: string;
  allow_pickup: boolean;
  allow_shipping: boolean;
  available_sizes: string;
  metadata: Record<string, unknown>;
};

const createEmptyProductForm = (): ProductFormState => ({
  slug: '',
  sku: '',
  title: '',
  short_description: '',
  description: '',
  product_type: 'physical',
  price_cents: '0.00',
  image_url: '',
  external_url: '',
  external_label: '',
  status: 'draft',
  sort_order: '0',
  allow_pickup: true,
  allow_shipping: true,
  available_sizes: '',
  metadata: {},
});

const parseAvailableSizes = (metadata: Record<string, unknown> | null | undefined): string[] => {
  const raw = metadata?.available_sizes;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => String(value || '').trim())
    .filter(Boolean);
};

const getExternalStoreUrl = (metadata: Record<string, unknown> | null | undefined) => {
  const externalUrl = metadata && typeof metadata.external_url === 'string' ? metadata.external_url.trim() : '';
  return externalUrl || '';
};

const getExternalStoreLabel = (metadata: Record<string, unknown> | null | undefined) => {
  const externalLabel = metadata && typeof metadata.external_label === 'string' ? metadata.external_label.trim() : '';
  return externalLabel || '';
};

const formatDollarsInput = (amountCents: number) => (Math.max(0, amountCents) / 100).toFixed(2);

const formatCurrency = (amountCents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Math.max(0, amountCents) / 100);

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const centsToCsvAmount = (amountCents?: number | null) => ((Number(amountCents) || 0) / 100).toFixed(2);

const csvEscape = (value: unknown) => {
  const normalized = String(value ?? '');
  if (!/[",\n]/.test(normalized)) return normalized;
  return `"${normalized.replace(/"/g, '""')}"`;
};

const toCsvDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
};

const toCsvFilenameDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');
  return `${year}${month}${day}-${hour}${minute}`;
};

const formatShippingAddressForCsv = (shippingAddress: Record<string, unknown> | null) => {
  if (!shippingAddress || typeof shippingAddress !== 'object') return '';
  const preferredParts = [
    shippingAddress.line1,
    shippingAddress.line2,
    shippingAddress.city,
    shippingAddress.state,
    shippingAddress.postalCode,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  if (preferredParts.length > 0) {
    return preferredParts.join(', ');
  }

  return Object.entries(shippingAddress)
    .map(([key, value]) => `${key}:${String(value ?? '').trim()}`)
    .filter((entry) => !entry.endsWith(':'))
    .join(', ');
};

const getSelectedSize = (metadata: Record<string, unknown> | null | undefined) => {
  const selectedSize = metadata && typeof metadata.selected_size === 'string' ? metadata.selected_size.trim() : '';
  return selectedSize || '';
};

const buildOrdersCsv = (orders: WebsiteStoreOrder[]) => {
  const headers = [
    'order_id',
    'placed_at',
    'paid_at',
    'order_status',
    'payment_status',
    'payment_provider',
    'payment_order_id',
    'payment_capture_id',
    'fulfillment_method',
    'fulfillment_status',
    'contact_name',
    'contact_email',
    'contact_phone',
    'subtotal_usd',
    'shipping_usd',
    'total_usd',
    'currency',
    'shipping_address',
    'notes',
    'item_product_title',
    'item_product_slug',
    'item_product_type',
    'item_selected_size',
    'item_quantity',
    'item_unit_price_usd',
    'item_line_total_usd',
  ];

  const rows = orders.flatMap((order) => {
    const items = Array.isArray(order.store_order_items) && order.store_order_items.length > 0
      ? order.store_order_items
      : [null];

    return items.map((item) => [
      order.id,
      toCsvDate(order.created_at),
      toCsvDate(order.paid_at),
      order.status,
      order.payment_status,
      order.payment_provider,
      order.payment_order_id || '',
      order.payment_capture_id || '',
      order.fulfillment_method,
      order.fulfillment_status,
      order.contact_name,
      order.contact_email,
      order.contact_phone || '',
      centsToCsvAmount(order.subtotal_cents),
      centsToCsvAmount(order.shipping_amount_cents),
      centsToCsvAmount(order.total_cents),
      order.currency,
      formatShippingAddressForCsv(order.shipping_address),
      order.notes || '',
      item?.product_title || '',
      item?.product_slug || '',
      item?.product_type || '',
      item ? getSelectedSize(item.metadata) : '',
      item?.quantity ?? '',
      item ? centsToCsvAmount(item.unit_price_cents) : '',
      item ? centsToCsvAmount(item.line_total_cents) : '',
    ]);
  });

  return [headers, ...rows]
    .map((row) => row.map((value) => csvEscape(value)).join(','))
    .join('\r\n');
};

async function uploadStoreProductImage(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `website-store-products/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error: uploadError } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
  return data.publicUrl;
}

const paymentTone = (status: string): 'success' | 'warning' | 'neutral' | 'danger' => {
  const normalized = String(status || '').toLowerCase();
  if (['captured', 'completed'].includes(normalized)) return 'success';
  if (['approved', 'order_created', 'awaiting_order', 'capture_pending'].includes(normalized)) return 'warning';
  if (['payment_failed', 'capture_denied', 'order_cancelled', 'expired'].includes(normalized)) return 'danger';
  return 'neutral';
};

export default function WebsiteStoreManagementPage() {
  const { products, isLoading: isLoadingProducts, error: productsError, saveProduct, isSaving, deleteProduct } = useWebsiteStoreProducts();
  const { settings, isLoading: isLoadingSettings, error: settingsError, updateSettings, isUpdating } = useWebsiteStoreSettings();
  const { orders, isLoading: isLoadingOrders, error: ordersError, updateOrder, isUpdating: isUpdatingOrder } = useWebsiteStoreOrders();
  const [productForm, setProductForm] = useState<ProductFormState>(createEmptyProductForm);
  const [settingsForm, setSettingsForm] = useState({
    storefront_title: '',
    storefront_blurb: '',
    pickup_enabled: true,
    pickup_label: '',
    pickup_instructions: '',
    flat_shipping_enabled: true,
    flat_shipping_cents: '0',
    support_email: '',
    support_phone: '',
  });
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = useState<string | null>(null);
  const [settingsErrorNotice, setSettingsErrorNotice] = useState<string | null>(null);
  const [orderNotice, setOrderNotice] = useState<string | null>(null);
  const [orderActionError, setOrderActionError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activeSection, setActiveSection] = useState<'products' | 'orders' | 'settings'>('products');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!settings) return;
    setSettingsForm({
      storefront_title: settings.storefront_title || '',
      storefront_blurb: settings.storefront_blurb || '',
      pickup_enabled: settings.pickup_enabled,
      pickup_label: settings.pickup_label || '',
      pickup_instructions: settings.pickup_instructions || '',
      flat_shipping_enabled: settings.flat_shipping_enabled,
      flat_shipping_cents: String(settings.flat_shipping_cents ?? 0),
      support_email: settings.support_email || '',
      support_phone: settings.support_phone || '',
    });
  }, [settings]);

  const selectedOrder = useMemo(
    () => orders.find((order) => order.id === selectedOrderId) ?? orders[0] ?? null,
    [orders, selectedOrderId],
  );
  const publishedProductCount = useMemo(
    () => products.filter((product) => product.status === 'published').length,
    [products],
  );
  const activeOrderCount = useMemo(
    () => orders.filter((order) => order.fulfillment_status !== 'fulfilled' && order.fulfillment_status !== 'cancelled').length,
    [orders],
  );

  useEffect(() => {
    if (!selectedOrderId && orders[0]?.id) {
      setSelectedOrderId(orders[0].id);
    }
  }, [orders, selectedOrderId]);

  const resetProductForm = () => {
    setProductForm(createEmptyProductForm());
    setFormNotice(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEditProduct = (product: WebsiteStoreProduct) => {
    setActiveSection('products');
    const metadata = (product.metadata ?? {}) as Record<string, unknown>;
    setProductForm({
      id: product.id,
      slug: product.slug,
      sku: product.sku || '',
      title: product.title,
      short_description: product.short_description || '',
      description: product.description || '',
      product_type: product.product_type,
      price_cents: formatDollarsInput(product.price_cents),
      image_url: product.image_url || '',
      external_url: getExternalStoreUrl(metadata),
      external_label: getExternalStoreLabel(metadata),
      status: product.status,
      sort_order: String(product.sort_order),
      allow_pickup: product.allow_pickup,
      allow_shipping: product.allow_shipping,
      available_sizes: parseAvailableSizes(metadata).join(', '),
      metadata,
    });
    setFormNotice(null);
    setFormError(null);
  };

  const handleProductImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') || !ALLOWED_TYPES.includes(file.type)) {
      setFormError('Please choose a JPEG, PNG, WebP, or GIF image.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFormError('Image must be smaller than 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploadingImage(true);
      setFormError(null);
      const imageUrl = await uploadStoreProductImage(file);
      setProductForm((current) => ({ ...current, image_url: imageUrl }));
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not upload product image.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveProduct = async () => {
    if (!productForm.title.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!productForm.slug.trim()) {
      setFormError('Slug is required.');
      return;
    }

    try {
      setFormError(null);
      const parsedPriceDollars = Number.parseFloat(productForm.price_cents || '0');
      if (!Number.isFinite(parsedPriceDollars) || parsedPriceDollars < 0) {
        setFormError('Enter a valid price in dollars.');
        return;
      }
      const priceCents = Math.round(parsedPriceDollars * 100);
      const externalUrl = productForm.external_url.trim();
      const externalLabel = productForm.external_label.trim();
      const availableSizes = productForm.product_type === 'physical'
        ? productForm.available_sizes.split(',').map((value) => value.trim()).filter(Boolean)
        : [];
      const metadata: Record<string, unknown> = { ...productForm.metadata };
      if (availableSizes.length > 0) {
        metadata.available_sizes = availableSizes;
      } else {
        delete metadata.available_sizes;
      }
      if (externalUrl) {
        metadata.external_url = externalUrl;
      } else {
        delete metadata.external_url;
      }
      if (externalLabel) {
        metadata.external_label = externalLabel;
      } else {
        delete metadata.external_label;
      }
      await saveProduct({
        id: productForm.id,
        slug: productForm.slug.trim(),
        sku: productForm.sku.trim() || null,
        title: productForm.title.trim(),
        short_description: productForm.short_description.trim() || null,
        description: productForm.description.trim() || null,
        product_type: productForm.product_type,
        price_cents: priceCents,
        currency: 'USD',
        image_url: productForm.image_url.trim() || null,
        status: productForm.status,
        sort_order: Number(productForm.sort_order || 0),
        allow_pickup: productForm.product_type === 'digital' || externalUrl ? false : productForm.allow_pickup,
        allow_shipping: productForm.product_type === 'digital' || externalUrl ? false : productForm.allow_shipping,
        metadata,
      });
      setFormNotice(productForm.id ? 'Product updated.' : 'Product created.');
      if (!productForm.id) resetProductForm();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not save product.');
    }
  };

  const handleDeleteProduct = async (product: WebsiteStoreProduct) => {
    if (!window.confirm(`Delete ${product.title}?`)) return;
    try {
      await deleteProduct(product.id);
      if (productForm.id === product.id) resetProductForm();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not delete product.');
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsErrorNotice(null);
      await updateSettings({
        storefront_title: settingsForm.storefront_title.trim() || 'Store',
        storefront_blurb: settingsForm.storefront_blurb.trim() || null,
        pickup_enabled: settingsForm.pickup_enabled,
        pickup_label: settingsForm.pickup_label.trim() || 'Church pickup',
        pickup_instructions: settingsForm.pickup_instructions.trim() || null,
        flat_shipping_enabled: settingsForm.flat_shipping_enabled,
        flat_shipping_cents: Math.max(0, Number(settingsForm.flat_shipping_cents || 0)),
        support_email: settingsForm.support_email.trim() || null,
        support_phone: settingsForm.support_phone.trim() || null,
      });
      setSettingsNotice('Store settings updated.');
    } catch (error) {
      setSettingsErrorNotice(error instanceof Error ? error.message : 'Could not update store settings.');
    }
  };

  const handleOrderUpdate = async (order: WebsiteStoreOrder, updates: Partial<WebsiteStoreOrder>) => {
    try {
      await updateOrder({
        id: order.id,
        status: updates.status,
        fulfillment_status: updates.fulfillment_status,
        notes: updates.notes ?? order.notes,
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Could not update order.');
    }
  };

  const handleExportOrdersCsv = () => {
    if (orders.length === 0) {
      setOrderActionError('There are no store orders to export yet.');
      setOrderNotice(null);
      return;
    }

    try {
      const csv = buildOrdersCsv(orders);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `store-orders-${toCsvFilenameDate()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      setOrderNotice(`Exported ${orders.length} store order${orders.length === 1 ? '' : 's'} to CSV.`);
      setOrderActionError(null);
    } catch (error) {
      setOrderActionError(error instanceof Error ? error.message : 'Could not export store orders.');
      setOrderNotice(null);
    }
  };

  return (
    <Page>
      <Header>
        <HeaderMain>
          <HeaderKicker>Commerce Admin</HeaderKicker>
          <HeaderTitle>Website Store</HeaderTitle>
          <HelperText>
            Manage products shown on the public website store, adjust pickup or flat shipping settings, and review incoming orders.
          </HelperText>
          <HeaderStats>
            <HeaderStat>
              <strong>{products.length}</strong>
              <span>Total products</span>
            </HeaderStat>
            <HeaderStat>
              <strong>{publishedProductCount}</strong>
              <span>Published live</span>
            </HeaderStat>
            <HeaderStat>
              <strong>{activeOrderCount}</strong>
              <span>Open orders</span>
            </HeaderStat>
          </HeaderStats>
        </HeaderMain>

        <HeaderActions>
          <Button
            type="button"
            onClick={() => {
              resetProductForm();
              setActiveSection('products');
            }}
          >
            Add new product
          </Button>
          <Button type="button" $variant="secondary" onClick={() => setActiveSection('settings')}>
            View store settings
          </Button>
        </HeaderActions>
      </Header>

      <TabStrip>
        <TabRow>
          <TabButton type="button" $active={activeSection === 'products'} onClick={() => setActiveSection('products')}>Products</TabButton>
          <TabButton type="button" $active={activeSection === 'orders'} onClick={() => setActiveSection('orders')}>Orders</TabButton>
        </TabRow>
      </TabStrip>

      {activeSection === 'products' && (
        <Grid>
          <Card>
            <CardTitle>{productForm.id ? 'Edit product' : 'New product'}</CardTitle>
            <CardHint>Products are stored in the shared website store tables and displayed on the public site when published.</CardHint>
            <FieldGrid>
              <FormSection>
                <Field>
                  Title
                  <Input value={productForm.title} onChange={(event) => setProductForm((current) => ({ ...current, title: event.target.value }))} placeholder="Faith Journal" />
                </Field>
                <InlineGrid>
                  <Field>
                    Slug
                    <Input value={productForm.slug} onChange={(event) => setProductForm((current) => ({ ...current, slug: event.target.value }))} placeholder="faith-journal" />
                  </Field>
                  <Field>
                    SKU
                    <Input value={productForm.sku} onChange={(event) => setProductForm((current) => ({ ...current, sku: event.target.value }))} placeholder="STORE-001" />
                  </Field>
                </InlineGrid>
                <Field>
                  Short description
                  <Input value={productForm.short_description} onChange={(event) => setProductForm((current) => ({ ...current, short_description: event.target.value }))} placeholder="A premium hardcover notebook for sermon notes." />
                </Field>
                <Field>
                  Description
                  <TextArea value={productForm.description} onChange={(event) => setProductForm((current) => ({ ...current, description: event.target.value }))} placeholder="Share details about what buyers receive, materials, and fulfillment notes." />
                </Field>
              </FormSection>
              <FormSection>
                <InlineGrid>
                  <Field>
                    Product type
                    <Select value={productForm.product_type} onChange={(event) => setProductForm((current) => ({ ...current, product_type: event.target.value as 'physical' | 'digital' }))}>
                      <option value="physical">Physical</option>
                      <option value="digital">Digital</option>
                    </Select>
                  </Field>
                  <Field>
                    Status
                    <Select value={productForm.status} onChange={(event) => setProductForm((current) => ({ ...current, status: event.target.value as ProductFormState['status'] }))}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </Field>
                </InlineGrid>
                <InlineGrid>
                  <Field>
                    Price (USD)
                    <Input type="number" min="0" step="0.01" value={productForm.price_cents} onChange={(event) => setProductForm((current) => ({ ...current, price_cents: event.target.value }))} />
                  </Field>
                  <Field>
                    Sort order
                    <Input type="number" value={productForm.sort_order} onChange={(event) => setProductForm((current) => ({ ...current, sort_order: event.target.value }))} />
                  </Field>
                </InlineGrid>
                <Field>
                  Image URL
                  <Input value={productForm.image_url} onChange={(event) => setProductForm((current) => ({ ...current, image_url: event.target.value }))} placeholder="https://..." />
                </Field>
                <Field>
                  External purchase URL
                  <Input value={productForm.external_url} onChange={(event) => setProductForm((current) => ({ ...current, external_url: event.target.value }))} placeholder="https://amazon.com/..." />
                </Field>
                <Field>
                  External button label
                  <Input value={productForm.external_label} onChange={(event) => setProductForm((current) => ({ ...current, external_label: event.target.value }))} placeholder="Buy on Amazon" />
                </Field>
                <Actions>
                  <UploadButtonLabel className={uploadingImage ? 'disabled' : ''}>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleProductImageChange} />
                    {uploadingImage ? 'Uploading…' : 'Upload image'}
                  </UploadButtonLabel>
                </Actions>
                <CardHint>
                  Leave external purchase URL blank for normal cart checkout. When set, the product opens that link instead of using PayPal checkout in the store.
                </CardHint>
              </FormSection>
              {productForm.product_type === 'physical' && !productForm.external_url.trim() && (
                <FormSection>
                  <Field>
                    Available sizes
                    <Input
                      value={productForm.available_sizes}
                      onChange={(event) => setProductForm((current) => ({ ...current, available_sizes: event.target.value }))}
                      placeholder="S, M, L, XL"
                    />
                  </Field>
                  <InlineGrid>
                    <CheckboxRow>
                      <input type="checkbox" checked={productForm.allow_pickup} onChange={(event) => setProductForm((current) => ({ ...current, allow_pickup: event.target.checked }))} />
                      Allow pickup
                    </CheckboxRow>
                    <CheckboxRow>
                      <input type="checkbox" checked={productForm.allow_shipping} onChange={(event) => setProductForm((current) => ({ ...current, allow_shipping: event.target.checked }))} />
                      Allow shipping
                    </CheckboxRow>
                  </InlineGrid>
                </FormSection>
              )}
              {formError ? <Notice $error>{formError}</Notice> : null}
              {formNotice ? <Notice>{formNotice}</Notice> : null}
              <FormActions>
                <Button type="button" onClick={handleSaveProduct} disabled={isSaving}>{isSaving ? 'Saving…' : productForm.id ? 'Save product' : 'Create product'}</Button>
                <Button type="button" $variant="secondary" onClick={resetProductForm}>Clear</Button>
              </FormActions>
            </FieldGrid>
          </Card>

          <Card>
            <CardTitle>Products</CardTitle>
            <CardHint>Published products are visible on the public store. Draft and archived products stay in admin only.</CardHint>
            {productsError ? <Notice $error>{String(productsError)}</Notice> : null}
            {isLoadingProducts ? <Notice>Loading products…</Notice> : null}
            {!isLoadingProducts && (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Type</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id}>
                        <td>
                          <ProductCell>
                            <ProductThumb $src={product.image_url} />
                            <div>
                              <ProductTitle>{product.title}</ProductTitle>
                              <Muted>/{product.slug}</Muted>
                              <Muted>{product.short_description || product.sku || 'No short description'}</Muted>
                              {parseAvailableSizes((product.metadata ?? {}) as Record<string, unknown>).length > 0 ? (
                                <Muted>Sizes: {parseAvailableSizes((product.metadata ?? {}) as Record<string, unknown>).join(', ')}</Muted>
                              ) : null}
                              {getExternalStoreUrl((product.metadata ?? {}) as Record<string, unknown>) ? (
                                <Muted>External: {getExternalStoreLabel((product.metadata ?? {}) as Record<string, unknown>) || 'Open product link'}</Muted>
                              ) : null}
                            </div>
                          </ProductCell>
                        </td>
                        <td>{getExternalStoreUrl((product.metadata ?? {}) as Record<string, unknown>) ? `external / ${product.product_type}` : product.product_type}</td>
                        <td>{getExternalStoreUrl((product.metadata ?? {}) as Record<string, unknown>) && product.price_cents <= 0 ? 'External purchase' : formatCurrency(product.price_cents, product.currency)}</td>
                        <td>
                          <StatusPill $tone={product.status === 'published' ? 'success' : product.status === 'archived' ? 'neutral' : 'warning'}>
                            {product.status}
                          </StatusPill>
                        </td>
                        <td>
                          <RowActions>
                            <Button type="button" $variant="secondary" onClick={() => handleEditProduct(product)}>Edit</Button>
                            <Button type="button" $variant="secondary" onClick={() => saveProduct({ id: product.id, status: product.status === 'published' ? 'draft' : 'published' })}>
                              {product.status === 'published' ? 'Unpublish' : 'Publish'}
                            </Button>
                            <Button type="button" $variant="danger" onClick={() => handleDeleteProduct(product)}>Delete</Button>
                          </RowActions>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr>
                        <td colSpan={5}>
                          <Notice>No store products yet.</Notice>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </Grid>
      )}

      {activeSection === 'settings' && (
        <SingleColumn>
          <Card>
            <CardTitle>Store settings</CardTitle>
            <CardHint>These settings control the public storefront title, pickup availability, and flat-shipping amount.</CardHint>
            {settingsError ? <Notice $error>{String(settingsError)}</Notice> : null}
            {isLoadingSettings ? <Notice>Loading settings…</Notice> : null}
            {!isLoadingSettings && (
              <FieldGrid>
                <Field>
                  Storefront title
                  <Input value={settingsForm.storefront_title} onChange={(event) => setSettingsForm((current) => ({ ...current, storefront_title: event.target.value }))} />
                </Field>
                <Field>
                  Storefront blurb
                  <TextArea value={settingsForm.storefront_blurb} onChange={(event) => setSettingsForm((current) => ({ ...current, storefront_blurb: event.target.value }))} />
                </Field>
                <CheckboxRow>
                  <input type="checkbox" checked={settingsForm.pickup_enabled} onChange={(event) => setSettingsForm((current) => ({ ...current, pickup_enabled: event.target.checked }))} />
                  Enable pickup
                </CheckboxRow>
                <Field>
                  Pickup label
                  <Input value={settingsForm.pickup_label} onChange={(event) => setSettingsForm((current) => ({ ...current, pickup_label: event.target.value }))} />
                </Field>
                <Field>
                  Pickup instructions
                  <TextArea value={settingsForm.pickup_instructions} onChange={(event) => setSettingsForm((current) => ({ ...current, pickup_instructions: event.target.value }))} />
                </Field>
                <CheckboxRow>
                  <input type="checkbox" checked={settingsForm.flat_shipping_enabled} onChange={(event) => setSettingsForm((current) => ({ ...current, flat_shipping_enabled: event.target.checked }))} />
                  Enable flat shipping
                </CheckboxRow>
                <Field>
                  Flat shipping amount (cents)
                  <Input type="number" min="0" value={settingsForm.flat_shipping_cents} onChange={(event) => setSettingsForm((current) => ({ ...current, flat_shipping_cents: event.target.value }))} />
                </Field>
                <InlineGrid>
                  <Field>
                    Support email
                    <Input value={settingsForm.support_email} onChange={(event) => setSettingsForm((current) => ({ ...current, support_email: event.target.value }))} />
                  </Field>
                  <Field>
                    Support phone
                    <Input value={settingsForm.support_phone} onChange={(event) => setSettingsForm((current) => ({ ...current, support_phone: event.target.value }))} />
                  </Field>
                </InlineGrid>
                {settingsErrorNotice ? <Notice $error>{settingsErrorNotice}</Notice> : null}
                {settingsNotice ? <Notice>{settingsNotice}</Notice> : null}
                <Actions>
                  <Button type="button" onClick={handleSaveSettings} disabled={isUpdating}>{isUpdating ? 'Saving…' : 'Save settings'}</Button>
                  <Button type="button" $variant="secondary" onClick={() => setActiveSection('products')}>Back to products</Button>
                </Actions>
              </FieldGrid>
            )}
          </Card>
        </SingleColumn>
      )}

      {activeSection === 'orders' && (
        <SingleColumn>
          <Card>
            <CardTitle>Orders</CardTitle>
            <CardHint>Review recent website store orders and update fulfillment when items are ready or shipped.</CardHint>
            {ordersError ? <Notice $error>{String(ordersError)}</Notice> : null}
            {orderActionError ? <Notice $error>{orderActionError}</Notice> : null}
            {orderNotice ? <Notice>{orderNotice}</Notice> : null}
            {isLoadingOrders ? <Notice>Loading orders…</Notice> : null}
            {!isLoadingOrders && (
              <>
                <Actions style={{ marginBottom: '1rem' }}>
                  <Button type="button" $variant="secondary" onClick={handleExportOrdersCsv} disabled={orders.length === 0}>
                    Export CSV
                  </Button>
                </Actions>
                <OrderCardGrid>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Fulfillment</th>
                        <th>Placed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} onClick={() => setSelectedOrderId(order.id)} style={{ cursor: 'pointer', background: selectedOrder?.id === order.id ? 'var(--surfaceVariant)' : undefined }}>
                          <td>
                            <ProductTitle>{order.contact_name}</ProductTitle>
                            <Muted>{order.contact_email}</Muted>
                          </td>
                          <td>{formatCurrency(order.total_cents, order.currency)}</td>
                          <td>
                            <StatusPill $tone={paymentTone(order.payment_status)}>{order.payment_status}</StatusPill>
                          </td>
                          <td>
                            <StatusPill $tone={order.fulfillment_status === 'fulfilled' ? 'success' : order.fulfillment_status === 'cancelled' ? 'danger' : 'warning'}>
                              {order.fulfillment_status}
                            </StatusPill>
                          </td>
                          <td>{formatDateTime(order.created_at)}</td>
                        </tr>
                      ))}
                      {orders.length === 0 && (
                        <tr>
                          <td colSpan={5}><Notice>No store orders yet.</Notice></td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </TableWrap>

                {selectedOrder && (
                  <div>
                    <DetailGrid>
                      <DetailItem>
                        <DetailLabel>Customer</DetailLabel>
                        <DetailValue>{selectedOrder.contact_name}\n{selectedOrder.contact_email}\n{selectedOrder.contact_phone || 'No phone provided'}</DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Payment</DetailLabel>
                        <DetailValue>
                          {selectedOrder.payment_provider} · {selectedOrder.payment_status}\nOrder ID: {selectedOrder.payment_order_id || '—'}\nCapture ID: {selectedOrder.payment_capture_id || '—'}
                        </DetailValue>
                      </DetailItem>
                      <DetailItem>
                        <DetailLabel>Fulfillment</DetailLabel>
                        <DetailValue>
                          {selectedOrder.fulfillment_method} · {selectedOrder.fulfillment_status}\nPaid: {formatDateTime(selectedOrder.paid_at)}
                        </DetailValue>
                      </DetailItem>
                    </DetailGrid>

                    {selectedOrder.fulfillment_method === 'shipping' && selectedOrder.shipping_address ? (
                      <DetailItem style={{ marginTop: '1rem' }}>
                        <DetailLabel>Shipping address</DetailLabel>
                        <DetailValue>{JSON.stringify(selectedOrder.shipping_address, null, 2)}</DetailValue>
                      </DetailItem>
                    ) : null}

                    <div style={{ marginTop: '1rem' }}>
                      <CardTitle style={{ fontSize: '1rem' }}>Items</CardTitle>
                      <TableWrap>
                        <Table>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Qty</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedOrder.store_order_items ?? []).map((item) => (
                              <tr key={item.id}>
                                <td>
                                  <ProductTitle>{item.product_title}</ProductTitle>
                                  <Muted>{item.product_type}</Muted>
                                  {typeof item.metadata?.selected_size === 'string' && item.metadata.selected_size.trim() ? (
                                    <Muted>Size: {item.metadata.selected_size.trim()}</Muted>
                                  ) : null}
                                </td>
                                <td>{item.quantity}</td>
                                <td>{formatCurrency(item.line_total_cents, selectedOrder.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    </div>

                    <Actions style={{ marginTop: '1rem' }}>
                      <Button type="button" $variant="secondary" disabled={isUpdatingOrder} onClick={() => handleOrderUpdate(selectedOrder, { fulfillment_status: 'processing' })}>Mark processing</Button>
                      <Button type="button" $variant="secondary" disabled={isUpdatingOrder} onClick={() => handleOrderUpdate(selectedOrder, { fulfillment_status: selectedOrder.fulfillment_method === 'pickup' ? 'ready_for_pickup' : 'fulfilled', status: selectedOrder.status === 'pending' ? 'paid' : selectedOrder.status })}>
                        {selectedOrder.fulfillment_method === 'pickup' ? 'Ready for pickup' : 'Mark fulfilled'}
                      </Button>
                      <Button type="button" disabled={isUpdatingOrder} onClick={() => handleOrderUpdate(selectedOrder, { fulfillment_status: 'fulfilled', status: selectedOrder.status === 'pending' ? 'paid' : selectedOrder.status })}>Complete order</Button>
                      <Button type="button" $variant="danger" disabled={isUpdatingOrder} onClick={() => handleOrderUpdate(selectedOrder, { fulfillment_status: 'cancelled', status: 'cancelled' })}>Cancel order</Button>
                    </Actions>
                  </div>
                )}
                </OrderCardGrid>
              </>
            )}
          </Card>
        </SingleColumn>
      )}
    </Page>
  );
}